import { useCallback, useEffect, useRef, useState } from "react";
import { ProposalUnicaEsito } from "@/components/ProposalUnicaEsito";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError, eur, API } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { effectiveQuoteState } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, FileText, ShieldCheck, Lock, CheckCircle2, Circle, Send, Package,
  FileDown, Wallet, Loader2, AlertTriangle, CalendarClock, CreditCard, Copy,
  Sparkles, Mail, RefreshCw, FileSignature, Bot, ArrowRight, ClipboardList, Handshake,
} from "lucide-react";

const SINISTRI_STATO = { liquidato: "Liquidato", riservato: "Riservato / in corso", senza_seguito: "Senza seguito" };

// Etichette Needs Analysis (allineate a backend/needs_analysis.py)
const NA_LABELS = {
  tipo_ente: { label: "Tipo di ente", opts: { ets_odv_aps: "ETS / OdV / APS", coop_sociale: "Cooperativa sociale", parrocchia: "Parrocchia / ente religioso", impresa_bottega: "Impresa / Bottega del Mondo", altro: "Altro" } },
  proteggere: { label: "Cosa proteggere", opts: { responsabilita_terzi: "Responsabilità verso terzi", persone: "Le persone (volontari, soci, dipendenti)", beni_sede: "I beni / la sede", territorio_catnat: "Il territorio da eventi catastrofali" } },
  volontari_soci: { label: "Volontari / soci attivi", opts: { molti: "Sì, molti", pochi: "Sì, pochi", no: "No" } },
  beni_valore: { label: "Sede o beni di valore", opts: { si: "Sì", no: "No" } },
};

function NeedsAnalysisView({ na }) {
  const a = na.answers || {};
  const row = (code) => {
    const meta = NA_LABELS[code]; if (!meta) return null;
    const v = a[code];
    if (v == null || (Array.isArray(v) && v.length === 0)) return null;
    const text = Array.isArray(v) ? v.map((x) => meta.opts[x] || x).join(", ") : (meta.opts[v] || v);
    return (
      <div key={code} className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-forest/60 sm:w-40">{meta.label}</span>
        <span className="text-sm text-forest">{text}</span>
      </div>
    );
  };
  return (
    <div className="space-y-2">
      {["tipo_ente", "proteggere", "volontari_soci", "beni_valore"].map(row)}
      {(na.suggeriti || []).length > 0 && (
        <div className="mt-3 border-t border-border/50 pt-3">
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-forest/60">Prodotti suggeriti</div>
          <div className="flex flex-wrap gap-1.5">
            {na.suggeriti.map((s) => (
              <span key={s.code} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.level === "consigliato" ? "bg-forest text-white" : "bg-amber-100 text-amber-800"}`}>{s.name}</span>
            ))}
          </div>
        </div>
      )}
      {na.updated_at && (
        <p className="mt-2 text-[11px] text-muted-foreground">Compilata il {new Date(na.updated_at).toLocaleDateString("it-IT")}{na.compilata_da ? ` · ${na.compilata_da}` : ""}</p>
      )}
    </div>
  );
}

const COVERAGE_LABELS = {
  rct: "RCT", rco: "RCO", organi_sociali: "Organi Sociali", tutela_legale: "Tutela Legale",
  rc: "RC verso Terzi", morte: "Caso Morte", ip: "Invalidità Permanente",
  ricovero: "Diaria da Ricovero", immob: "Diaria Immobilizzazione", rsm: "Rimborso Spese Mediche",
  inabilita: "Inabilità Temporanea", immobilizzazione: "Diaria Immobilizzazione", spese: "Rimborso Spese Mediche",
};
const PRODUCT_NAMES = {
  rc_economia_solidale: "RC Economia Solidale",
  mrv_volontariato: "Tutela Multirischi del Volontariato",
  infortuni_economia_solidale: "Infortuni per l'Economia Solidale",
  cat_nat_impresa: "CAT NAT Assimoco",
  multirischi_beni: "Tutela Multirischi Danni ai Beni",
  casa_multirischi: "Tutela Multirischi per l'Abitazione",
  cooperazione_salute: "Cooperazione Salute 4You",
  d_o: "D&O Directors & Officers",
  eticapro_infortuni_formula_service: "Eticapro Infortuni — Formula Service",
};
const MB_SEZ_LABEL = { incendio: "Incendio e altri danni ai beni", furto: "Furto e Rapina", elettronica: "Elettronica", energie: "Energie Rinnovabili", rc: "Responsabilità Civile", tutela_legale: "Tutela Legale", pet: "Pet", assistenza: "Assistenza" };
const ACTION_LABELS = {
  created: "Preventivo creato", modificato: "Preventivo modificato", emesso: "Preventivo emesso",
  bundle_built: "Bundle documentale generato", referral_sent: "Inviato in referral tecnico",
  referral_decision: "Decisione referral", autorizzato: "Autorizzato dall'Agenzia",
  stato_forzato: "Stato aggiornato dall'Agenzia", accettato_cliente: "Accettato dal cliente",
  accettato_partner: "Accettato dal partner", richiesta_emissione: "Richiesta di emissione",
  incasso_confermato: "Incasso confermato", polizza_emessa: "Polizza emessa",
  consegna_perfezionata: "Consegna perfezionata", email_inviata: "Email inviata al cliente",
  crm_sync: "Sincronizzato con CRM", relazione_generata: "Relazione tecnica generata",
  inviato_agenzia: "Inviato all'ufficio assunzione dell'Agenzia",
  inviato_cliente: "Preventivo inviato al cliente",
};
const BUNDLE_DOCS_RC = [
  ["mup_id", "MUP IVASS (Assietica principale + Partner collaboratore)"],
  ["cga_id", "Condizioni Generali (CGA)"],
  ["dip_id", "DIP Danni"],
  ["dip_agg_id", "DIP aggiuntivo"],
];
const BUNDLE_DOCS_MRV = [
  ["mup_id", "MUP IVASS (Assietica principale + Partner collaboratore)"],
  ["cga_id", "Condizioni Generali (CGA)"],
  ["dip_id", "DIP Danni"],
  ["dip_agg_id", "DIP aggiuntivo"],
];

export default function CaseFile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [busy, setBusy] = useState("");
  const [emitOpen, setEmitOpen] = useState(false);
  const [emit, setEmit] = useState({ data_effetto: new Date().toISOString().slice(0, 10), frazionamento: "annuale", prima_scadenza_31_12: false });
  const [rel, setRel] = useState({ testo: "", sito: "", model: "gpt-5.4", loaded: false });
  const [punti, setPunti] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [provLordo, setProvLordo] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/quotes/${id}`);
    setQuote(data);
    const { data: t } = await api.get(`/quotes/${id}/timeline`);
    setTimeline(t);
    const { data: r } = await api.get(`/quotes/${id}/relazione`);
    setRel({ testo: r.relazione_testo || "", sito: r.sito_web || "", model: r.model || "gpt-5.4", loaded: true });
    api.get(`/products/${data.product_code}/punti-attenzione`).then(({ data: pa }) => setPunti(pa)).catch(() => setPunti({ punti: [] }));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, label) => {
    setBusy(label);
    try { await fn(); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const buildBundle = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/bundle`);
    if (data.completo) toast.success("Bundle generato e completo");
    else toast.warning("MUP generato. Mancano documenti di prodotto (caricabili dall'Agenzia).");
  }, "bundle");
  const emetti = () => act(async () => { await api.post(`/quotes/${id}/emetti`); toast.success("Preventivo emesso"); }, "emetti");
  const referral = () => act(async () => { await api.post(`/quotes/${id}/referral`, { note: "Invio questionario tecnico" }); toast.success("Referral inviato all'Agenzia"); }, "referral");
  const inviaAgenzia = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/invia-agenzia`);
    toast.success(data.email_sent ? `Inviato all'Agenzia (${data.destinatario})` : "Referral registrato — email non configurata");
  }, "invia-agenzia");
  const inviaCliente = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/invia-cliente`);
    toast.success(`Proposta inviata al cliente (${data.destinatario}) con ${(data.allegati || []).length} allegati — copia al Partner`);
  }, "invia-cliente");
  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand("copy"); document.body.removeChild(ta); return ok;
      } catch { return false; }
    }
  };
  const copiaLink = async () => {
    const token = quote?.accept_token;
    const link = token ? `${window.location.origin}/proposta/${token}` : window.location.href;
    const ok = await copyText(link);
    if (ok) toast.success(token ? "Link cliente copiato — invialo al tuo cliente" : "Link fascicolo copiato");
    else toast.error("Copia non riuscita, copia manualmente: " + link);
  };
  const accettaCliente = () => act(async () => { await api.post(`/quotes/${id}/accetta-cliente`); }, "ac");
  const salvaProvvigione = () => act(async () => {
    await api.post(`/quotes/${id}/provvigione`, { lordo: parseFloat(provLordo) });
    setProvLordo("");
    toast.success("Provvigione lordo registrata");
  }, "prov");
  const fatturaProvvigione = () => act(async () => {
    await api.post(`/quotes/${id}/provvigione/fattura`);
    toast.success("Fattura segnalata all'Agenzia");
  }, "prov-fatt");
  const liquidaProvvigione = () => act(async () => {
    await api.post(`/quotes/${id}/provvigione/liquida`);
    toast.success("Bonifico provvigioni registrato");
  }, "prov-liq");
  const attivaOtp = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/adesione/link`);
    const url = `${window.location.origin}${data.path}`;
    await copyText(url);
    toast.success("Link firma a distanza (OTP) creato e copiato");
  }, "otp");
  const accettaPartner = () => act(async () => { await api.post(`/quotes/${id}/accetta-partner`); }, "ap");
  const richiediEmissione = () => act(async () => {
    await api.post(`/quotes/${id}/richiedi-emissione`, emit);
    setEmitOpen(false);
    toast.success("Richiesta di emissione inviata. Generata la richiesta 'Paga la Polizza'.");
  }, "emis");
  const consegna = () => act(async () => { await api.post(`/quotes/${id}/consegna`); toast.success("Pratica perfezionata"); }, "consegna");
  const inviaPagamentoCliente = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/invia-pagamento-cliente`);
    toast.success(`Avviso di pagamento inviato al cliente (${data.destinatario})`);
  }, "invia-pagamento");
  const contrattoRef = useRef(null);
  const provaRef = useRef(null);
  const [scontoForm, setScontoForm] = useState(null);
  const [appForm, setAppForm] = useState(null);
  const caricaContratto = (file) => {
    if (!file) return;
    act(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", "contratto");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/quotes/${id}/contratto`, { file_id: data.storage_path });
      toast.success("Contratto caricato — il Partner è stato notificato");
    }, "contratto");
  };

  const genRelazione = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/relazione/genera`, { sito_web: rel.sito, model: rel.model });
    setRel((s) => ({ ...s, testo: data.relazione_testo, sito: data.sito_web, model: data.model || s.model }));
    toast.success(`Relazione generata con ${data.model_label || "AI"}`);
  }, "rel-gen");
  const saveRelazione = () => act(async () => {
    await api.put(`/quotes/${id}/relazione`, { relazione_testo: rel.testo, sito_web: rel.sito });
    toast.success("Relazione salvata");
  }, "rel-save");
  const sendEmail = () => act(async () => {
    await api.post(`/quotes/${id}/email`, { recipient: emailTo });
    setEmailOpen(false);
    toast.success("Email inviata (simulata)");
  }, "email");
  const crmSync = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/crm-sync`);
    toast.success(`Sincronizzato al CRM (${data.external_id})`);
  }, "crm");

  const salvaVersione = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/salva-versione`);
    toast.success(`Nuova versione salvata (V${data.versione})`);
  }, "versione");
  const congelaProposta = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/congela-proposta`);
    toast.success(`Proposta congelata: ${data.proposta.numero}`);
  }, "congela");
  const riapriProposta = () => act(async () => {
    const { data } = await api.post(`/quotes/${id}/riapri-proposta`);
    toast.success(`Proposta riaperta — nuova versione V${data.versione}`);
  }, "riapri");

  const salvaSconto = () => act(async () => {
    await api.post(`/quotes/${id}/sconto-convenzione`, scontoForm || {});
    toast.success("Registro sconto convenzione salvato");
  }, "sconto");
  const salvaAppartenenza = (extra) => act(async () => {
    await api.post(`/quotes/${id}/appartenenza`, { ...(appForm || {}), ...(extra || {}) });
    toast.success("Verifica appartenenza aggiornata");
  }, "appart");
  const caricaProva = (file) => {
    if (!file) return;
    act(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", "prova_appartenenza");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/quotes/${id}/appartenenza`, { ...(appForm || {}), prova_file_id: data.storage_path });
      toast.success("Prova di appartenenza archiviata");
    }, "prova");
  };

  const openPdf = (path) => window.open(`${API}${path}`, "_blank");

  if (!quote) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  const premium = quote.calc_result?.premium;
  const bundle = quote.bundle || {};
  const bundleDocs = BUNDLE_DOCS_RC;
  const EDIT_ROUTE = {
    rc_economia_solidale: "/quota/nuovo", mrv_volontariato: "/quota/mrv",
    infortuni_economia_solidale: "/quota/infortuni", cat_nat_impresa: "/quota/catnat",
    multirischi_beni: "/quota/multirischi", casa_multirischi: "/quota/casa", cooperazione_salute: "/quota/cooperazione-salute", d_o: "/quota/do",
  };
  const lockedByState = ["pagato", "polizza_emessa", "perfezionato", "non_perfezionato"].includes(quote.stato);
  // Il partner non può modificare un preventivo in referral/autorizzazione: sola lettura fino alla decisione dell'Agenzia.
  const referralLockedForPartner = quote.binario === "referral_tecnico" && !isAdmin;
  const frozen = !!quote.proposta;
  const canEdit = !lockedByState && !referralLockedForPartner && !frozen;
  const sf = scontoForm || quote.sconto_convenzione || {};
  const af = appForm || quote.appartenenza || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/preventivi" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-forest">
          <ChevronLeft className="h-4 w-4" /> Torna ai preventivi
        </Link>
        <div className="flex items-center gap-3">
          {canEdit && (
            <Button variant="outline" data-testid="edit-quote-button"
              onClick={() => navigate(`${quote.pf_input ? "/formula-self" : EDIT_ROUTE[quote.product_code] || "/quota/nuovo"}?edit=${quote.id}`)}
              className="gap-1.5 rounded-xl border-forest/30 text-forest hover:bg-sage">
              <RefreshCw className="h-4 w-4" /> Modifica preventivo
            </Button>
          )}
          {referralLockedForPartner && !lockedByState && (
            <span data-testid="readonly-hint" className="text-xs font-medium text-amber-700">Sola lettura · in autorizzazione</span>
          )}
          <StatusBadge state={effectiveQuoteState(quote)} />
        </div>
      </div>

      <ProposalUnicaEsito tid={quote.trattativa_id} currentId={quote.id} />
      <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest" data-testid="casefile-codice-prev">
            {quote.codice_prev || `Fascicolo #${quote.id.slice(-6).toUpperCase()}`}
            {quote.versione && <span className="ml-2 align-middle text-base font-medium text-forest/60">V{quote.versione}</span>}
            {frozen && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-forest px-2.5 py-1 align-middle text-xs font-medium text-white" data-testid="casefile-proposta-badge">
                <Lock className="h-3 w-3" /> {quote.proposta.numero}
              </span>
            )}
          </h1>
          <p className="mt-1 text-muted-foreground">{quote.anagrafica?.ragione_sociale} · {PRODUCT_NAMES[quote.product_code] || "RC Economia Solidale"}</p>
        </div>
        <div className="text-right">
          <div className="font-heading text-3xl font-semibold text-forest">{eur(quote.premio_finale)}</div>
          <div className={`text-xs font-medium ${quote.binario === "standard" ? "text-emerald-600" : "text-amber-600"}`}>
            {quote.binario === "standard" ? "Binario standard" : "Referral tecnico"}
          </div>
        </div>
      </div>

      {quote.binario === "referral_tecnico" && (
        <div data-testid="referral-banner" className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Preventivo in referral tecnico</p>
                <p className="text-xs text-amber-800">
                  {quote.referral?.motivo ? `Motivo: ${quote.referral.motivo}. ` : ""}
                  Richiede la valutazione dell'ufficio assunzione dell'Agenzia prima dell'emissione.
                </p>
                {quote.inviato_agenzia_at && (
                  <p className="mt-1 text-xs font-medium text-emerald-700" data-testid="referral-sent-info">
                    ✓ Inviato all'Agenzia il {new Date(quote.inviato_agenzia_at).toLocaleString("it-IT")}
                    {quote.referral?.destinatario ? ` — ${quote.referral.destinatario}` : ""}
                  </p>
                )}
              </div>
            </div>
            <Button onClick={inviaAgenzia} disabled={busy === "invia-agenzia"} data-testid="invia-agenzia-button"
              className="shrink-0 gap-1.5 bg-amber-600 hover:bg-amber-700">
              {busy === "invia-agenzia" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {quote.inviato_agenzia_at ? "Invia di nuovo" : "Invia all'Agenzia"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Premio */}
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Dettaglio premio</h2>
            {quote.product_code === "d_o" ? (
              <div className="space-y-1 text-sm" data-testid="do-casefile-detail">
                <div className="flex justify-between"><span className="text-muted-foreground">Indice di rischio</span><span className="font-medium text-forest">{quote.calc_result?.rischio?.livello || "—"}</span></div>
                {(quote.calc_result?.premium?.rows || []).map((r, i) => (
                  <div key={i} className="flex justify-between gap-2"><span className="text-muted-foreground">{r.voce}</span><span className="text-forest">{eur(r.net)}</span></div>
                ))}
                <div className="border-t pt-1 text-xs font-semibold uppercase tracking-wide text-forest">Documenti per la Direzione</div>
                {(quote.do_documenti || []).length ? quote.do_documenti.map((d) => (
                  <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.storage_path}`} target="_blank" rel="noreferrer" data-testid={`do-doc-link-${d.id}`} className="block text-xs text-forest underline">{d.nome}</a>
                )) : <div className="text-xs text-amber-700">Nessun documento caricato</div>}
                <div className="text-xs text-amber-700">Prodotto riservato alla Direzione: invia la pratica all'Agenzia per l'autorizzazione.</div>
              </div>
            ) : quote.product_code === "cooperazione_salute" ? (
              <div className="space-y-1 text-sm" data-testid="cs-casefile-detail">
                <div className="flex justify-between"><span className="text-muted-foreground">Piano proposto</span><span className="font-medium text-forest">{quote.calc_result?.opzioni?.[quote.calc_result?.scelta]?.nome}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Persone</span><span className="text-forest">{(quote.calc_result?.persone || []).map((p) => `${p.alias} (${p.eta ?? p.fascia})`).join(", ")}</span></div>
                {(quote.calc_result?.piano?.rows || []).map((r, i) => (
                  <div key={i} className="flex justify-between"><span className="text-muted-foreground">{r.voce} · {r.quando}</span><span className="text-forest">{eur(r.importo)}</span></div>
                ))}
                <div className="flex justify-between border-t pt-1"><span className="text-muted-foreground">Detrazione 19% annua</span><span className="text-forest">{eur(quote.calc_result?.detrazione)}</span></div>
              </div>
            ) : ["multirischi_beni", "casa_multirischi"].includes(quote.product_code) ? (
              (quote.calc_result?.sezioni && Object.keys(quote.calc_result.sezioni).length) ? (
                <div className="space-y-3">
                  {Object.entries(quote.calc_result.sezioni).map(([scode, sez]) => (
                    <div key={scode}>
                      <div className="text-xs font-semibold uppercase tracking-wide text-forest/70">{MB_SEZ_LABEL[scode] || scode}</div>
                      {sez.rows.map((r, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{r.label}</span>
                          <span className="text-forest">imponibile {eur(r.net)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm text-forest/80"><span>Lordo sezione</span><span className="font-medium">{eur(sez.gross_total)}</span></div>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-border pt-2 font-medium text-forest">
                    <span>Totale lordo di polizza</span><span>{eur(premium?.gross_total)}</span>
                  </div>
                </div>
              ) : <p className="text-sm text-muted-foreground">Tariffazione riservata alla Direzione.</p>
            ) : premium?.breakdown ? (
              <div className="space-y-1">
                {(Array.isArray(premium.breakdown)
                  ? premium.breakdown.map((b, i) => [`${premium.n_ubicazioni > 1 ? `Ub. ${b.ubicazione} · ` : ""}${b.evento_label} — ${b.partita_label}`, b, i])
                  : Object.entries(premium.breakdown).map(([k, b]) => [COVERAGE_LABELS[k] || k, b, k])
                ).map(([label, b, key]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="text-forest">imponibile {eur(b.net)} · <span className="font-medium">{eur(b.gross)}</span></span>
                  </div>
                ))}
                <div className="mt-2 flex justify-between border-t border-border pt-2 font-medium text-forest">
                  <span>Totale lordo</span><span>{eur(premium.gross_total)}</span>
                </div>
              </div>
            ) : premium ? (
              <div className="flex justify-between font-medium text-forest">
                <span>Totale lordo annuo</span><span>{eur(premium.gross_total)}</span>
              </div>
            ) : <p className="text-sm text-muted-foreground">Tariffazione riservata alla Direzione.</p>}
            {quote.provvigione && (
              <div className="mt-4 flex items-center justify-between rounded-lg border border-terracotta/30 bg-terracotta/5 px-3 py-2">
                <span className="flex items-center gap-1.5 text-xs font-medium text-terracotta"><Wallet className="h-3.5 w-3.5" /> Provvigione ({quote.provvigione.pct}%)</span>
                <span className="font-heading font-semibold text-terracotta">{eur(quote.provvigione.importo)}</span>
              </div>
            )}
          </Card>

          {/* Analisi esigenze (needs analysis salvata) */}
          {quote.anagrafica?.needs_analysis?.answers && (
            <Card className="rounded-xl border-border/60 p-5" data-testid="casefile-needs-analysis">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest">
                <ClipboardList className="h-4 w-4" /> Analisi esigenze
              </h2>
              <NeedsAnalysisView na={quote.anagrafica.needs_analysis} />
            </Card>
          )}

          {/* Versioni & Proposta congelata (Fase 3) */}
          <Card className="rounded-xl border-border/60 p-5" data-testid="casefile-versioni">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-forest">
                <FileSignature className="h-4 w-4" /> Versioni & Proposta
              </h2>
              <span className="text-xs font-medium text-forest/60">{quote.codice_prev} · V{quote.versione || 1}</span>
            </div>

            {frozen ? (
              <div className="rounded-lg border border-forest/30 bg-sage/30 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-forest">
                  <Lock className="h-4 w-4" /> Proposta {quote.proposta.numero} congelata
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Istantanea della versione V{quote.proposta.versione_origine} · premio {eur(quote.proposta.snapshot?.premio_finale)}
                  {quote.proposta.frozen_at ? ` · ${new Date(quote.proposta.frozen_at).toLocaleDateString("it-IT")}` : ""}.
                  Non modificabile: riaprila per apportare modifiche.
                </p>
                {!lockedByState && (
                  <Button variant="outline" data-testid="riapri-proposta-button" onClick={riapriProposta} disabled={busy === "riapri"}
                    className="mt-3 gap-1.5 rounded-xl border-forest/30 text-forest hover:bg-sage">
                    {busy === "riapri" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Riapri proposta
                  </Button>
                )}
              </div>
            ) : (
              canEdit && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" data-testid="salva-versione-button" onClick={salvaVersione} disabled={busy === "versione"}
                    className="gap-1.5 rounded-xl border-forest/30 text-forest hover:bg-sage">
                    {busy === "versione" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Salva come nuova versione
                  </Button>
                  <Button data-testid="congela-proposta-button" onClick={congelaProposta} disabled={busy === "congela"}
                    className="gap-1.5 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                    {busy === "congela" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Congela in Proposta
                  </Button>
                </div>
              )
            )}

            {(quote.versioni?.length > 0 || quote.proposte_storiche?.length > 0) && (
              <div className="mt-4 space-y-2 border-t border-border/50 pt-3">
                <div className="text-xs font-medium uppercase tracking-wide text-forest/60">Storico</div>
                {(quote.versioni || []).map((v, i) => (
                  <div key={`v${i}`} className="flex items-center justify-between text-sm" data-testid={`versione-storica-${v.versione}`}>
                    <span className="text-muted-foreground">Versione V{v.versione}{v.note ? ` — ${v.note}` : ""}</span>
                    <span className="text-forest">{eur(v.premio_finale)}</span>
                  </div>
                ))}
                {(quote.proposte_storiche || []).map((p, i) => (
                  <div key={`p${i}`} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Proposta {p.numero} (riaperta{p.motivo ? ` — ${p.motivo}` : ""})</span>
                    <span className="text-forest">{eur(p.snapshot?.premio_finale)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>



          {/* Bundle blindato */}
          <Card className="rounded-xl border-border/60 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-forest"><Lock className="h-4 w-4" /> Bundle documentale IVASS</h2>
              {bundle.completo ? <span className="text-xs font-medium text-emerald-600">Completo</span> : <span className="text-xs font-medium text-amber-600">Incompleto</span>}
            </div>
            <div className="space-y-2">
              {bundleDocs.map(([key, label]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  {bundle[key] ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
                  <span className={bundle[key] ? "text-forest" : "text-muted-foreground"}>{label}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={buildBundle} disabled={busy === "bundle"} data-testid="build-bundle-button" className="gap-1.5">
                {busy === "bundle" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />} Genera bundle (MUP)
              </Button>
              {bundle.mup_id && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => openPdf(`/quotes/${id}/mup`)} className="gap-1.5 text-forest"><FileDown className="h-4 w-4" /> MUP</Button>
                  <Button size="sm" variant="ghost" onClick={() => openPdf(`/quotes/${id}/pdf?view=partner`)} data-testid="pdf-partner-button" className="gap-1.5 text-forest"><FileText className="h-4 w-4" /> Preventivo (partner)</Button>
                  <Button size="sm" variant="ghost" onClick={() => openPdf(`/quotes/${id}/pdf?view=cliente`)} data-testid="pdf-cliente-button" className="gap-1.5 text-forest"><FileText className="h-4 w-4" /> Preventivo (cliente)</Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={copiaLink} data-testid="copy-link-button" className="gap-1.5 text-forest"><Copy className="h-4 w-4" /> Copia link fascicolo</Button>
              <Button size="sm" variant="ghost" onClick={inviaCliente} disabled={busy === "invia-cliente"} data-testid="invia-cliente-button" className="gap-1.5 text-forest">
                {busy === "invia-cliente" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia proposta al cliente
              </Button>
            </div>
            {!bundle.completo && (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Emissione bloccata finché il bundle non è completo (blindatura IVASS non disattivabile).
              </p>
            )}
          </Card>

          {/* Azioni ciclo di vita */}
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Prossima azione</h2>
            <div className="flex flex-wrap gap-2">
              {(quote.stato === "bozza" || quote.stato === "autorizzato") && quote.binario === "standard" && (
                <Button onClick={emetti} disabled={busy === "emetti" || !bundle.completo} data-testid="emetti-button" className="gap-1.5 bg-terracotta hover:bg-terracotta-hover">
                  <ShieldCheck className="h-4 w-4" /> Emetti preventivo
                </Button>
              )}
              {quote.stato === "bozza" && quote.binario === "referral_tecnico" && (
                <Button onClick={referral} disabled={busy === "referral"} data-testid="referral-button" className="gap-1.5 bg-amber-600 hover:bg-amber-700">
                  <Send className="h-4 w-4" /> Invia referral tecnico
                </Button>
              )}
              {quote.stato === "referral_inviato" && <p className="text-sm text-muted-foreground">In attesa di valutazione della Direzione.</p>}
              {quote.stato === "referral_rifiutato" && <p className="text-sm text-red-600">Referral rifiutato dall'Agenzia.</p>}
              {quote.stato === "autorizzato" && <p className="text-sm text-emerald-700">Autorizzato dall'Agenzia: puoi generare il bundle ed emettere.</p>}
              {quote.stato === "preventivo_emesso" && (
                <Button onClick={accettaCliente} disabled={busy === "ac"} data-testid="accetta-cliente-button" className="gap-1.5 bg-forest hover:bg-forest-hover">Accettazione cliente</Button>
              )}
              {quote.stato === "accettato_cliente" && (
                <Button onClick={() => setEmitOpen(true)} data-testid="richiedi-emissione-button" className="gap-1.5 bg-forest hover:bg-forest-hover">
                  <CalendarClock className="h-4 w-4" /> Richiedi emissione contratto
                </Button>
              )}
              {quote.stato === "in_attesa_bonifico" && <p className="text-sm text-muted-foreground">In attesa che l'Agenzia confermi il bonifico.</p>}
              {quote.stato === "pagato" && <p className="text-sm text-muted-foreground">Pagato. In attesa emissione polizza dall'Agenzia.</p>}
              {quote.stato === "polizza_emessa" && (
                <Button onClick={consegna} disabled={busy === "consegna"} data-testid="consegna-button" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">Consegna al cliente</Button>
              )}
              {quote.stato === "perfezionato" && <p className="flex items-center gap-1.5 text-sm text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Pratica perfezionata.</p>}
              {quote.stato === "non_perfezionato" && <p className="flex items-center gap-1.5 text-sm text-red-600"><AlertTriangle className="h-4 w-4" /> Pratica non perfezionata (chiusa dall'Agenzia).</p>}
            </div>
          </Card>

          {/* Punti d'attenzione dalle CGA (AI, grounded) */}
          {punti && punti.punti?.length > 0 && (
            <Card className="rounded-xl border-amber-200 bg-amber-50/40 p-5" data-testid="punti-attenzione-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-amber-900">
                <AlertTriangle className="h-4 w-4" /> Punti d'attenzione dalle CGA
              </h2>
              <ul className="space-y-2">
                {punti.punti.map((p, i) => (
                  <li key={i} data-testid={`punto-${i}`} className="flex gap-2 text-sm text-amber-900/90">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-amber-200 pt-2 text-[11px] text-amber-800/80">
                Sintesi generata da AI sulle Condizioni di polizza — verifica sempre sulle CGA.
              </p>
            </Card>
          )}

          {/* Dichiarazione sinistri (secondaria, non invasiva) */}
          {quote.sinistri && quote.sinistri.ha_sinistri !== null && quote.sinistri.ha_sinistri !== undefined && (
            <Card className="rounded-xl border-border/60 p-4" data-testid="sinistri-card">
              <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">
                <AlertTriangle className="h-3.5 w-3.5" /> Dichiarazione sinistri
              </h2>
              {quote.sinistri.ha_sinistri ? (
                <div className="space-y-1.5">
                  {(quote.sinistri.elenco || []).map((s, i) => (
                    <div key={i} data-testid={`sinistro-${i}`} className="flex flex-wrap gap-x-2 text-sm text-forest/90">
                      <span className="font-medium">{s.anno || "—"}</span>
                      <span>· {s.tipo || "sinistro"}</span>
                      {s.importo ? <span>· € {s.importo}</span> : null}
                      <span>· {SINISTRI_STATO[s.stato] || s.stato}</span>
                      {s.descrizione ? <span className="text-muted-foreground">— {s.descrizione}</span> : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-emerald-700">Nessun sinistro dichiarato negli ultimi {quote.sinistri.periodo_anni || 5} anni.</p>
              )}
            </Card>
          )}

          {/* Valutazione di adeguatezza IDD (secondaria) */}
          {quote.idd && quote.idd.adeguatezza && (
            <Card className="rounded-xl border-border/60 p-4" data-testid="idd-card">
              <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">
                <ShieldCheck className="h-3.5 w-3.5" /> Valutazione di adeguatezza (IDD)
              </h2>
              <p className="text-sm text-forest/90">
                <span className={quote.idd.adeguatezza === "adeguato" ? "font-medium text-emerald-700" : "font-medium text-amber-700"}>
                  {quote.idd.adeguatezza === "adeguato" ? "Adeguato ai bisogni assicurativi" : "Non adeguato"}
                </span>
                {quote.idd.nota ? <span className="text-muted-foreground"> — {quote.idd.nota}</span> : null}
              </p>
            </Card>
          )}

          {/* Dicitura precontrattuale obbligatoria */}
          <div data-testid="pre-adesione-notice" className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-3 text-xs font-medium text-terracotta">
            Prima dell'adesione leggere attentamente le Condizioni di polizza, il DIP e il DIP aggiuntivo allegati alla presente, unitamente al MUP.
          </div>

          {/* Relazione tecnica AI + integrazioni */}
          <Card className="rounded-xl border-border/60 p-5" data-testid="relazione-card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-forest"><FileSignature className="h-4 w-4" /> Relazione tecnica</h2>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/assistente?product=${quote.product_code}`)} data-testid="assistant-link-button" className="gap-1.5"><Bot className="h-4 w-4" /> Assistente</Button>
                <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="email-button" className="gap-1.5"><Mail className="h-4 w-4" /> Email</Button>
                <Button size="sm" variant="outline" onClick={crmSync} disabled={busy === "crm"} data-testid="crm-sync-button" className="gap-1.5">
                  {busy === "crm" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} CRM
                </Button>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
              <Input data-testid="relazione-sito" placeholder="Sito web dell'ente (per l'analisi AI)" value={rel.sito}
                onChange={(e) => setRel({ ...rel, sito: e.target.value })} />
              <Select value={rel.model} onValueChange={(v) => setRel({ ...rel, model: v })}>
                <SelectTrigger data-testid="relazione-model" className="w-full sm:w-[210px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-5.4">GPT-5.4 (sintetico)</SelectItem>
                  <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6 (dettagliato)</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={genRelazione} disabled={busy === "rel-gen"} data-testid="relazione-genera-button" className="gap-1.5 bg-forest hover:bg-forest-hover">
                {busy === "rel-gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {rel.testo ? "Rigenera con AI" : "Genera con AI"}
              </Button>
            </div>
            <Textarea data-testid="relazione-testo" className="mt-3 min-h-[160px] text-sm" placeholder="La relazione tecnica generata comparirà qui e potrà essere modificata."
              value={rel.testo} onChange={(e) => setRel({ ...rel, testo: e.target.value })} />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={saveRelazione} disabled={busy === "rel-save" || !rel.testo} data-testid="relazione-salva-button" className="gap-1.5">
                {busy === "rel-save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva
              </Button>
              {quote.relazione_testo && (
                <Button size="sm" variant="ghost" onClick={() => openPdf(`/quotes/${id}/relazione/pdf`)} data-testid="relazione-pdf-button" className="gap-1.5 text-forest"><FileDown className="h-4 w-4" /> Scarica PDF</Button>
              )}
            </div>
          </Card>

          {/* Paga la Polizza + piano rate */}
          {quote.pagamento && (
            <Card className="rounded-xl border-border/60 p-5" data-testid="payment-box">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><CreditCard className="h-4 w-4" /> Paga la Polizza</h2>
              <div className="rounded-lg bg-sage/50 p-4 text-sm">
                <p className="whitespace-pre-line text-forest">{quote.pagamento.messaggio || `Intestatario: ${quote.pagamento.intestatario || quote.pagamento.beneficiario}\nIBAN: ${quote.pagamento.iban}\nCausale: ${quote.pagamento.causale}`}</p>
              </div>
              {(quote.pagamento.numero_proposta || quote.proposta?.numero) && (
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="proposta-numeri">
                  {quote.pagamento.numero_proposta && (
                    <span className="text-muted-foreground">N. Proposta Compagnia: <span className="font-semibold text-forest">{quote.pagamento.numero_proposta}</span></span>
                  )}
                  {quote.proposta?.numero && (
                    <span className="text-muted-foreground">Proposta interna: <span className="font-semibold text-forest">{quote.proposta.numero}</span></span>
                  )}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid="copy-bonifico" onClick={() => { navigator.clipboard?.writeText(quote.pagamento.messaggio || `Intestatario: ${quote.pagamento.intestatario} - IBAN ${quote.pagamento.iban} - ${quote.pagamento.causale}`); toast.success("Messaggio di pagamento copiato: invialo al cliente"); }} className="gap-1.5">
                  <Copy className="h-4 w-4" /> Copia messaggio per il cliente
                </Button>
                {(isAdmin || user?.partner_id) && (
                  <Button size="sm" variant="outline" data-testid="invia-pagamento-cliente" onClick={inviaPagamentoCliente}
                    disabled={busy === "invia-pagamento" || !quote.pagamento.numero_proposta} className="gap-1.5 border-forest/30 text-forest">
                    {busy === "invia-pagamento" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia avviso di pagamento
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => window.open(quote.pagamento.pay_by_link?.url, "_blank")} className="gap-1.5 text-forest">
                  <CreditCard className="h-4 w-4" /> Pay by Link (mock)
                </Button>
              </div>
              {!quote.pagamento.numero_proposta && (
                <p data-testid="proposta-mancante" className="mt-2 text-[11px] text-amber-700">
                  In attesa del numero di Proposta dall'Agenzia: la causale sarà completata automaticamente una volta registrato.
                </p>
              )}
              <p className="mt-2 text-[11px] text-muted-foreground">Bonifico diretto ad Assietica (suggerito immediato/istantaneo). Pay by Link è PREDISPOSTO/MOCK.</p>
              <div data-testid="nota-provvigioni" className="mt-3 rounded-lg border border-forest/15 bg-sage/30 p-3 text-[11px] leading-relaxed text-forest/80">
                {quote.incasso_diretto ? (
                  <><span className="font-semibold text-forest">Incasso diretto Partner:</span> il premio è incassato direttamente dal Partner sul proprio conto, che effettua la rimessa all'Agenzia nei tempi concordati. Al cliente deve essere chiaro che il versamento è verso il Partner e non verso l'Agenzia. L'estratto conto provvigionale segue le regole del rapporto di collaborazione.</>
                ) : (
                  <><span className="font-semibold text-forest">Nota per il Partner (rapporto di collaborazione):</span> il cliente versa il premio direttamente all'Agenzia Assietica. Ricevuto il bonifico, l'Agenzia emette la polizza, la contabilizza e la trasmette al Partner per la raccolta delle firme. Entro il <b>05 del mese successivo</b> l'Agenzia invia al Partner l'estratto conto provvigionale; a fronte della fattura del Partner (al netto della ritenuta d'acconto) l'Agenzia procede al versamento delle provvigioni.</>
                )}
              </div>
              {quote.piano_rate && (
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Piano rate · frazionamento {quote.piano_rate.frazionamento}</span>
                    <span>1ª scad. annuale {quote.piano_rate.scadenza_annuale}</span>
                  </div>
                  <div className="space-y-1">
                    {quote.piano_rate.rate.map((r, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-1.5 text-sm">
                        <span className="text-forest">{r.descrizione}</span>
                        <span className="text-muted-foreground">scad. {r.scadenza} · <span className="font-medium text-forest">{eur(r.importo)}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Rete Solidale — registro sconto convenzione + verifica appartenenza */}
          <Card className="rounded-xl border-border/60 p-5" data-testid="casefile-convenzione">
            <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold text-forest">
              <Handshake className="h-4 w-4" /> Rete Solidale — sconto & appartenenza
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">Registro sconto per la tracciabilità verso la compagnia. Lo sconto non viene calcolato dal sistema né detratto dalle provvigioni. Nessun dato sanitario.</p>

            {/* Verifica appartenenza */}
            <div className={`mb-4 rounded-lg border p-3 ${af.verificata ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm font-semibold ${af.verificata ? "text-emerald-800" : "text-amber-800"}`}>
                  {af.verificata ? "Appartenenza verificata ✓" : "Appartenenza da verificare prima dell'emissione"}
                </span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Input data-testid="app-categoria" placeholder="Categoria (es. Volontario ETS)" value={af.categoria || ""}
                  onChange={(e) => setAppForm({ ...af, categoria: e.target.value })} />
                <Input data-testid="app-prova-tipo" placeholder="Tipo prova (attestazione, tessera, visura…)" value={af.prova_tipo || ""}
                  onChange={(e) => setAppForm({ ...af, prova_tipo: e.target.value })} />
              </div>
              <input ref={provaRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => caricaProva(e.target.files?.[0])} />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid="app-upload-prova" onClick={() => provaRef.current?.click()} disabled={busy === "prova"} className="gap-1.5 border-forest/30 text-forest">
                  <FileDown className="h-3.5 w-3.5 rotate-180" /> {af.prova_file_id ? "Sostituisci prova" : "Carica prova"}
                </Button>
                {af.prova_file_id && (
                  <Button size="sm" variant="ghost" data-testid="app-scarica-prova" onClick={() => openPdf(`/quotes/${id}/appartenenza-prova`)} className="gap-1.5 text-forest">
                    <FileDown className="h-3.5 w-3.5" /> Vedi prova
                  </Button>
                )}
                {!af.verificata ? (
                  <Button size="sm" data-testid="app-conferma" onClick={() => salvaAppartenenza({ verificata: true })} disabled={busy === "appart"} className="gap-1.5 bg-forest hover:bg-forest-hover">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Conferma appartenenza
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" data-testid="app-revoca" onClick={() => salvaAppartenenza({ verificata: false })} disabled={busy === "appart"} className="text-muted-foreground">Revoca</Button>
                )}
              </div>
            </div>

            {/* Registro sconto */}
            <div className="grid gap-2 sm:grid-cols-2">
              <Input data-testid="sconto-codice" placeholder="Codice convenzione (RSA-…)" value={sf.codice_convenzione || ""} onChange={(e) => setScontoForm({ ...sf, codice_convenzione: e.target.value.toUpperCase() })} />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="sconto-fascia" placeholder="Fascia (A/B)" value={sf.fascia || ""} onChange={(e) => setScontoForm({ ...sf, fascia: e.target.value })} />
                <Input data-testid="sconto-livello" placeholder="Livello (A1/A2/B)" value={sf.livello || ""} onChange={(e) => setScontoForm({ ...sf, livello: e.target.value })} />
              </div>
              <Input data-testid="sconto-premio-tariffa" type="number" placeholder="Premio di tariffa €" value={sf.premio_tariffa ?? ""} onChange={(e) => setScontoForm({ ...sf, premio_tariffa: e.target.value ? Number(e.target.value) : null })} />
              <Input data-testid="sconto-premio-finale" type="number" placeholder="Premio finale €" value={sf.premio_finale ?? ""} onChange={(e) => setScontoForm({ ...sf, premio_finale: e.target.value ? Number(e.target.value) : null })} />
              <Input data-testid="sconto-approvatore" placeholder="Approvatore" value={sf.approvatore || ""} onChange={(e) => setScontoForm({ ...sf, approvatore: e.target.value })} />
              <Input data-testid="sconto-riferimento" placeholder="Riferimento autorizzazione Assimoco" value={sf.riferimento_autorizzazione || ""} onChange={(e) => setScontoForm({ ...sf, riferimento_autorizzazione: e.target.value })} />
            </div>
            <div className="mt-3 flex items-center justify-between">
              {sf.registrato_at && <span className="text-[11px] text-muted-foreground">Registrato da {sf.registrato_da} il {new Date(sf.registrato_at).toLocaleDateString("it-IT")}</span>}
              <Button size="sm" data-testid="sconto-salva" onClick={salvaSconto} disabled={busy === "sconto"} className="ml-auto gap-1.5 bg-terracotta hover:bg-terracotta-hover">
                {busy === "sconto" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Registra sconto
              </Button>
            </div>
          </Card>


          {["pagato", "polizza_emessa", "perfezionato"].includes(quote.stato) && (
            <Card className="rounded-xl border-border/60 p-5" data-testid="contratto-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><FileSignature className="h-4 w-4" /> Contratto originale</h2>
              {quote.contratto_originale ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" data-testid="scarica-contratto" onClick={() => openPdf(`/quotes/${id}/contratto`)} className="gap-1.5 border-forest/30 text-forest">
                    <FileDown className="h-4 w-4" /> Scarica contratto da firmare
                  </Button>
                  {isAdmin && (
                    <>
                      <input ref={contrattoRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => caricaContratto(e.target.files[0])} />
                      <Button size="sm" variant="ghost" data-testid="sostituisci-contratto" onClick={() => contrattoRef.current?.click()} disabled={busy === "contratto"} className="gap-1.5 text-forest">
                        {busy === "contratto" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sostituisci
                      </Button>
                    </>
                  )}
                  <span className="text-[11px] text-muted-foreground">Caricato il {new Date(quote.contratto_originale.uploaded_at).toLocaleString("it-IT")}</span>
                </div>
              ) : isAdmin ? (
                <div>
                  <input ref={contrattoRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => caricaContratto(e.target.files[0])} />
                  <Button size="sm" variant="outline" data-testid="carica-contratto" onClick={() => contrattoRef.current?.click()} disabled={busy === "contratto"} className="gap-1.5 border-forest/30 text-forest">
                    {busy === "contratto" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Carica contratto originale
                  </Button>
                  <p className="mt-2 text-xs text-muted-foreground">Dopo l'incasso e la contabilizzazione, carica il PDF del contratto: il Partner lo scaricherà per raccogliere le firme.</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">In attesa che l'Agenzia carichi il contratto originale da firmare.</p>
              )}
              {isAdmin && (
                <div className="mt-3 border-t border-border/50 pt-3">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">In alternativa alla firma su carta:</p>
                  <Button size="sm" variant="ghost" data-testid="attiva-otp" onClick={attivaOtp} disabled={busy === "otp"} className="gap-1.5 text-forest">
                    {busy === "otp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Firma a distanza (OTP)
                  </Button>
                </div>
              )}
            </Card>
          )}

          {["pagato", "polizza_emessa", "perfezionato"].includes(quote.stato) && (
            <Card className="rounded-xl border-border/60 p-5" data-testid="provvigione-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><Wallet className="h-4 w-4" /> Provvigioni</h2>
              {quote.provvigione_conto?.lordo != null ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Lordo maturato</span>
                    <span className="font-semibold text-forest">{eur(quote.provvigione_conto.lordo)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Stato</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${quote.provvigione_conto.stato === "liquidata" ? "bg-emerald-100 text-emerald-700" : quote.provvigione_conto.stato === "fatturata" ? "bg-forest/10 text-forest" : "bg-amber-100 text-amber-800"}`}>
                      {quote.provvigione_conto.stato === "liquidata" ? "Liquidata" : quote.provvigione_conto.stato === "fatturata" ? "Fatturata" : "Maturata"}
                    </span>
                  </div>
                  {!isAdmin && quote.provvigione_conto.stato === "maturata" && (
                    <Button size="sm" variant="outline" data-testid="prov-fattura" onClick={fatturaProvvigione} disabled={busy === "prov-fatt"} className="mt-1 gap-1.5 border-forest/30 text-forest">
                      {busy === "prov-fatt" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Segna fattura emessa
                    </Button>
                  )}
                  {isAdmin && quote.provvigione_conto.stato === "fatturata" && (
                    <Button size="sm" data-testid="prov-liquida" onClick={liquidaProvvigione} disabled={busy === "prov-liq"} className="mt-1 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                      {busy === "prov-liq" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} Registra bonifico
                    </Button>
                  )}
                  {isAdmin && (
                    <div className="mt-2 flex items-center gap-2">
                      <Input type="number" value={provLordo} onChange={(e) => setProvLordo(e.target.value)} placeholder="Aggiorna lordo €" data-testid="prov-input-edit" className="h-9 w-40" />
                      <Button size="sm" variant="ghost" onClick={salvaProvvigione} disabled={busy === "prov" || !provLordo} className="text-forest">Aggiorna</Button>
                    </div>
                  )}
                </div>
              ) : isAdmin ? (
                <div className="flex items-center gap-2">
                  <Input type="number" value={provLordo} onChange={(e) => setProvLordo(e.target.value)} placeholder="Lordo provvigioni €" data-testid="prov-input" className="h-9 w-48" />
                  <Button size="sm" variant="outline" data-testid="prov-salva" onClick={salvaProvvigione} disabled={busy === "prov" || !provLordo} className="gap-1.5 border-forest/30 text-forest">
                    {busy === "prov" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Registra
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">L'Agenzia registrerà il lordo delle provvigioni maturate su questa polizza.</p>
              )}
            </Card>
          )}
        </div>

        {/* Timeline */}
        <Card className="h-fit rounded-xl border-border/60 p-5">
          <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Timeline</h2>
          <div className="space-y-4">
            {timeline.map((e, i) => {
              const d = e.detail || {};
              const hasVar = e.action === "modificato" && d.premio_nuovo != null && d.premio_precedente != null && d.premio_nuovo !== d.premio_precedente;
              const aumento = hasVar && d.premio_nuovo > d.premio_precedente;
              return (
                <div key={e.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`h-2.5 w-2.5 rounded-full ${e.action === "modificato" ? "bg-amber-500" : "bg-terracotta"}`} />
                    {i < timeline.length - 1 && <div className="my-1 w-px flex-1 bg-border" />}
                  </div>
                  <div className="pb-1">
                    <div className="text-sm font-medium text-forest">{ACTION_LABELS[e.action] || e.action}</div>
                    {hasVar && (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs" data-testid="timeline-premium-change">
                        <span className="text-muted-foreground line-through">{eur(d.premio_precedente)}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className={`font-semibold ${aumento ? "text-red-600" : "text-emerald-600"}`}>{eur(d.premio_nuovo)}</span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${aumento ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
                          {aumento ? "+" : "−"}{eur(Math.abs(d.premio_nuovo - d.premio_precedente))}
                        </span>
                      </div>
                    )}
                    <div className="mt-0.5 text-xs text-muted-foreground">{e.actor}</div>
                    <div className="text-[11px] text-muted-foreground">{new Date(e.at).toLocaleString("it-IT")}</div>
                  </div>
                </div>
              );
            })}
            {timeline.length === 0 && <p className="text-sm text-muted-foreground">Nessun evento.</p>}
          </div>
        </Card>
      </div>

      <Dialog open={emitOpen} onOpenChange={setEmitOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-forest">Richiedi emissione contratto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Data di effetto</Label>
              <Input type="date" data-testid="emit-data-effetto" value={emit.data_effetto} onChange={(e) => setEmit({ ...emit, data_effetto: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-forest">Prima scadenza al 31.12</div>
                <div className="text-xs text-muted-foreground">Rateo iniziale pro-rata, poi scadenza annuale al 31.12</div>
              </div>
              <Switch data-testid="emit-scadenza-3112" disabled={emit.frazionamento === "mensile"} checked={emit.prima_scadenza_31_12} onCheckedChange={(v) => setEmit({ ...emit, prima_scadenza_31_12: v })} />
            </div>
            {quote.product_code === "cat_nat_impresa" ? (
              <div className="rounded-lg border border-border/60 px-3 py-2.5" data-testid="emit-catnat-fraz">
                <div className="text-sm font-medium text-forest">Frazionamento (senza oneri)</div>
                <div className="mt-2 flex gap-2">
                  {["annuale", "semestrale", "mensile"].map((f) => {
                    const ok = f === "annuale" || quote.calc_result?.premium?.frazionamenti?.[f];
                    return (
                      <button key={f} disabled={!ok} onClick={() => setEmit({ ...emit, frazionamento: f, ...(f === "mensile" ? { prima_scadenza_31_12: false } : {}) })} data-testid={`emit-fraz-${f}`}
                        className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium capitalize disabled:opacity-40 ${emit.frazionamento === f ? "border-forest bg-forest text-white" : "border-border text-forest"}`}>{f}</button>
                    );
                  })}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">Semestrale con rata da almeno € 100 imponibili, mensile da almeno € 15 (Norme 2CE).</div>
              </div>
            ) : (
            <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-forest">Frazionamento semestrale</div>
                <div className="text-xs text-muted-foreground">
                  {(quote.premio_finale || 0) > 600 ? "Due rate + interessi di frazionamento (3%)" : "Disponibile solo per premi oltre €600"}
                </div>
              </div>
              <Switch data-testid="emit-frazionamento" disabled={(quote.premio_finale || 0) <= 600}
                checked={emit.frazionamento === "semestrale"}
                onCheckedChange={(v) => setEmit({ ...emit, frazionamento: v ? "semestrale" : "annuale" })} />
            </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={richiediEmissione} disabled={busy === "emis"} data-testid="emit-confirm-button" className="bg-terracotta hover:bg-terracotta-hover">
              {busy === "emis" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma e genera pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-forest">Invia preventivo via email</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Email destinatario</Label>
            <Input type="email" data-testid="email-recipient" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="cliente@esempio.it" />
            <p className="text-[11px] text-muted-foreground">Invio simulato (mock): registra l'evento nel fascicolo.</p>
          </div>
          <DialogFooter>
            <Button onClick={sendEmail} disabled={busy === "email" || !emailTo} data-testid="email-send-button" className="bg-forest hover:bg-forest-hover">
              {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
